import React, {
  createContext, useContext, useMemo, ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { useWindowDimensions } from 'common/hooks';
import { WindowSize } from 'common/hooks/types';

export type LayoutMode = 'mobile' | 'desktop';

export interface ResponsiveContextValue {
  windowSize: WindowSize;
  isDesktopView: boolean;
  isNativeApp: boolean;
  shouldExpandForWeb: boolean;
  layoutMode: LayoutMode;
}

const ResponsiveContext = createContext<ResponsiveContextValue | null>(null);

interface ResponsiveProviderProps {
  children: ReactNode;
}

// Carries derived breakpoint values only, never raw window measurements: a
// raw {width, height} here re-rendered every consumer on every iOS keyboard
// show/hide (see useWindowDimensions). Need a live size? Measure locally.
export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({ children }) => {
  const { windowSize, isDesktopView } = useWindowDimensions();
  const isNativeApp = Capacitor.isNativePlatform();

  const value = useMemo<ResponsiveContextValue>(() => {
    // Use desktop layout (top nav) when viewport is wide enough (> 768px)
    // This applies to both native apps (iPad landscape) and web
    const shouldExpandForWeb = isDesktopView;

    const layoutMode: LayoutMode = shouldExpandForWeb ? 'desktop' : 'mobile';

    return {
      windowSize,
      isDesktopView,
      isNativeApp,
      shouldExpandForWeb,
      layoutMode,
    };
  }, [windowSize, isDesktopView, isNativeApp]);

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
};

export const useResponsive = (): ResponsiveContextValue => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    throw new Error('useResponsive must be used within ResponsiveProvider');
  }
  return context;
};

export default ResponsiveContext;
